import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import PropTypes from 'prop-types';
import { uniqBy, debounce } from 'lodash-es';

import { AttachmentChipList } from './AttachmentChipList';
import { MORE_ATTACHMENTS_BTN_WIDTH, StyledAttachments, StyledEditorAttachments } from './style';

const MAX_DISPLAY_ATTACHMENTS_LIMIT = 3;

export const Attachments = (props) => {
  const {
    attachments,
    isReadOnly,
    isDeleted,
    isDownloaded,
    shouldTriggerDeleteApi,
    onAttachmentDeleted,
    deleteAttachmentEnabled,
  } = props;
  const wrapperRef = useRef(null);
  const [toggleMoreAttachments, setToggleMoreAttachments] = useState(false);

  const [displayAttachmentsLimit, setDisplayAttachmentsLimit] = useState(MAX_DISPLAY_ATTACHMENTS_LIMIT);
  const attachmentsLength = attachments?.length;
  const showMoreAttachments = attachmentsLength > displayAttachmentsLimit;
  const moreAttachmentsSum = attachmentsLength - displayAttachmentsLimit;
  let uniqueAttachments = attachments ? [...attachments] : [];
  if (isDeleted || isDownloaded) {
    uniqueAttachments = uniqBy(attachments, (attachment) => attachment.originalName);
  }

  const setLimit = useMemo(
    () =>
      debounce(() => {
        const limit = Math.floor((wrapperRef?.current?.offsetWidth - MORE_ATTACHMENTS_BTN_WIDTH) / 202);
        setDisplayAttachmentsLimit(Math.min(limit > 0 ? limit : 1, MAX_DISPLAY_ATTACHMENTS_LIMIT));
      }, 300),
    [],
  );

  useEffect(() => {
    setLimit();
  }, [setLimit]);

  useEffect(() => {
    window.addEventListener('resize', setLimit);
    return () => window.removeEventListener('resize', setLimit);
  }, [setLimit]);

  if (!attachments) {
    return null;
  }

  const attachmentChipList = (
    <AttachmentChipList
      attachments={
        !isReadOnly || toggleMoreAttachments ? uniqueAttachments : uniqueAttachments?.slice(0, displayAttachmentsLimit)
      }
      onAttachmentDeleted={onAttachmentDeleted}
      isReadOnly={isReadOnly}
      isDeleted={isDeleted}
      isDownloaded={isDownloaded}
      shouldTriggerDeleteApi={shouldTriggerDeleteApi}
      deleteAttachmentEnabled={deleteAttachmentEnabled}
    />
  );

  return !isReadOnly ? (
    <StyledEditorAttachments>{attachmentChipList}</StyledEditorAttachments>
  ) : (
    <StyledAttachments ref={wrapperRef}>
      {attachmentChipList}
      {showMoreAttachments && !toggleMoreAttachments && (
        <Button className="more-attachments-btn" onClick={() => setToggleMoreAttachments((prevToggle) => !prevToggle)}>
          +{moreAttachmentsSum}
        </Button>
      )}
    </StyledAttachments>
  );
};

Attachments.propTypes = {
  attachments: PropTypes.array,
  isReadOnly: PropTypes.bool,
  isDeleted: PropTypes.bool,
  isDownloaded: PropTypes.bool,
  onAttachmentDeleted: PropTypes.func,
};

Attachments.defaultProps = {
  attachments: [],
  onAttachmentDeleted: () => {},
  isReadOnly: true,
  isDeleted: false,
  isDownloaded: false,
};
